import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AblumDetail } from '../AblumDetail';
import { useAblumListHooks, usePremiumAblumHooks } from '../../hooks/dataHooks';
import { Icon } from '@shakkerai/components';
import { Text, Image, Menu, MenuTarget, MenuDropdown, CreateModal, MenuItem } from '@shakkerai/pro-components';
import styles from './index.modules.scss';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { useAlbumHooks } from '../../hooks/eventHooks';
import {
  ALBUM_EVENT_TYPE_DELETE,
  ALBUM_EVENT_TYPE_RENAME,
  useAlbumCardState,
  useComfirmModalState,
} from '../../store/base';
import { translate } from '@shakkerai/util-i18n';

export const UncategorizedGroupRender = (props) => {
  const { data } = props;

  if (!data) {
    return null;
  }

  const { imgInfoS } = data;

  return (
    <div className={styles.uncategorized}>
      <div className="flex items-center">
        <Text color="#333" fw={500} size={12}>
          ungrouped images
        </Text>
        <Text color="#7BD0C2" fw={500} size={12} className="ml-1">
          {imgInfoS.length}
        </Text>
        <InnerMenu onRename={() => console.info(`rename`)} onDelete={() => console.info(`rename`)}>
          <Icon className="icon-prodepot ml-2 cursor-pointer" color="#333"></Icon>
        </InnerMenu>
        <div className="grow"></div>
        {imgInfoS.length > 7 && (
          <Text color="#7BD0C2" className="cursor-pointer" fw={500} size={12}>
            More
          </Text>
        )}
      </div>

      <div className={styles.imageGroup}>
        {imgInfoS.slice(0, 7).map((i) => (
          <Image className={styles.image} key={i.imageId} src={convert2CompressedUrl(i.imageId, 200)}></Image>
        ))}
      </div>
    </div>
  );
};

const InnerMenu = (props) => {
  const { children, onRename, onDelete } = props;

  return (
    <Menu width={80}>
      <MenuTarget>{children}</MenuTarget>
      <MenuDropdown>
        <MenuItem data-role="gen-history-trigger" onClick={onRename}>
          Rename
        </MenuItem>

        <MenuItem data-role="gen-history-trigger" onClick={onDelete}>
          Delete
        </MenuItem>
      </MenuDropdown>
    </Menu>
  );
};

export const AblumCardRender = (props) => {
  const { data } = props;

  const coverImage = useMemo(() => {
    if (data?.imgInfoS.length > 0) {
      return data?.imgInfoS[0].imageId;
    } else {
      return '';
    }
  }, [data]);

  const { setCurrentEditCard } = useAlbumCardState();

  if (!data) {
    return null;
  }

  return (
    <div className={styles.ablumCard}>
      <Image className={styles.imagea} w={279} h={279} src={convert2CompressedUrl(coverImage, 300)}></Image>

      <div className={styles.float}>
        <div className={styles.shadow}></div>

        <div className={styles.content}>
          <div className={styles.top}>
            <Text iconClass="icon-protab" color="white" fw={500} size={12}>
              {data.groupName}
            </Text>

            <div className="flex">
              <Text color="#7BD0C2" fw={500} size={12} className="ml-1">
                {data?.imgInfoS.length}
              </Text>
              <Text color="white" fw={500} size={12} className="ml-1">
                Images
              </Text>
              <InnerMenu
                onRename={() => setCurrentEditCard(data, ALBUM_EVENT_TYPE_RENAME)}
                onDelete={() => setCurrentEditCard(data, ALBUM_EVENT_TYPE_DELETE)}
              >
                <Icon className="icon-prodepot ml-2 cursor-pointer" color="white"></Icon>
              </InnerMenu>
            </div>
          </div>

          <div className={styles.bottom}>
            {data?.imgInfoS.slice(0, 4).map((i) => (
              <div className={styles.imagec} key={i.imageId}>
                <Image h={55} w={55} radius="18px" src={convert2CompressedUrl(i.imageId, 60)}></Image>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const GroupRender = (props) => {
  const { listData } = props;

  if (!listData) {
    return null;
  }

  return (
    <div className={styles.group}>
      {listData.map((i) => (
        <AblumCardRender data={i} key={i.generateId}></AblumCardRender>
      ))}
    </div>
  );
};

export const AblumListRender = (props) => {
  const { data, onRefresh } = props;

  const uncategorizedGroupData = useMemo(() => {
    return data.list.find((item) => item.uncategorized);
  }, [data]);

  const albumListData = useMemo(() => {
    return data.list.filter((item) => !item.uncategorized);
  }, [data]);

  const [addVisible, setAddVisible] = useState(false);
  const [renameVisible, setRenameVisible] = useState(false);

  const { createAblum, deleteAblum, renameAblum } = useAlbumHooks();

  const { currentEditCard, eventType, clearEditCard } = useAlbumCardState();

  const { showComfirmModal, modalVisible } = useComfirmModalState();

  useEffect(() => {
    if (!modalVisible) {
      clearEditCard();
    }
  }, [modalVisible, clearEditCard]);

  useEffect(() => {
    if (eventType === ALBUM_EVENT_TYPE_DELETE) {
      if (currentEditCard) {
        showComfirmModal({
          content: translate('history_deletesingleimage'),
          subContent: translate('history_deletecheck_sub'),
          onConfirmCallback: () => {
            deleteAblum(currentEditCard.groupId, false);
          },
        });
      }
    } else if (eventType === ALBUM_EVENT_TYPE_RENAME) {
      if (currentEditCard) {
        setRenameVisible(true);
      } else {
        setRenameVisible(false);
      }
    }
  }, [currentEditCard, eventType, deleteAblum, showComfirmModal]);

  return (
    <div>
      <UncategorizedGroupRender data={uncategorizedGroupData}></UncategorizedGroupRender>
      <Text
        className={styles.new_album}
        onClick={() => setAddVisible(true)}
        iconClass="icon-a-proNewAlbum"
        color="#2c2c2c"
      >
        New Album
      </Text>

      <GroupRender listData={albumListData}></GroupRender>

      <CreateModal
        title="Add to my album"
        placeholder="Please enter the name of the new album..."
        opened={addVisible}
        onClose={() => setAddVisible(false)}
        onConfirm={async (value) => {
          createAblum(value);
          setAddVisible(false);
          onRefresh();
        }}
      ></CreateModal>

      <CreateModal
        title="Rename my album"
        placeholder="Please enter the name of the new album..."
        value="abababa"
        opened={renameVisible}
        onClose={() => clearEditCard()}
        onConfirm={async (value) => {
          await renameAblum(currentEditCard.groupId, value);
          clearEditCard();
          onRefresh();
        }}
      ></CreateModal>
    </div>
  );
};

/**
 * 负责请求列表的数据
 */
export const AblumList = forwardRef((props, ref) => {
  const { searchValueRef, sortValueRef, onDataUpdate } = props;

  const ablumDetailRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const { premiumAblumLoad, premiumAblumId } = usePremiumAblumHooks();

  const { reload } = useAblumListHooks(scrollContainerRef, searchValueRef, sortValueRef);

  useEffect(() => {
    premiumAblumLoad();
    reload();
    // eslint-disable-next-line
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      // 更新总数
      ablumDetailRef.current?.refresh();
    },
    reload: () => {
      // 搜索或者排序
      ablumDetailRef.current?.reload();
    },
  }));

  return (
    <div ref={ref}>
      {/* <div ref={scrollContainerRef} className={styles.ablumList}>
        {data && <AblumListRender data={data} onRefresh={reload}></AblumListRender>}
      </div> */}
      <AblumDetail
        ref={ablumDetailRef}
        searchValueRef={searchValueRef}
        sortValueRef={sortValueRef}
        onDataUpdate={onDataUpdate}
        premiumAblumId={premiumAblumId}
      />
    </div>
  );
});
